import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus, Search, MoreHorizontal, Trash2, UserPlus, Download,
  Users, AlertCircle, Filter, Loader2, UserCheck, UserX, Layers,
  Send, Hash, Eye, ToggleLeft, ExternalLink, CheckSquare
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

function UserProfileDialog({ user, groups, channels, open, onOpenChange, onRefresh }) {
  const [msgContent, setMsgContent] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviting, setInviting] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  if (!user) return null;

  const sendMsg = async () => {
    if (!msgContent.trim()) return;
    setSending(true);
    try {
      await api.post(`/users/send-message/${user.id}`, { content: msgContent });
      toast.success("Mesaj gonderildi");
      setMsgContent("");
    } catch (err) { toast.error(err.response?.data?.detail || "Mesaj gonderilemedi"); }
    finally { setSending(false); }
  };

  const inviteToChannel = async () => {
    if (!selectedChannel) return;
    setInviting(true);
    try {
      await api.post(`/users/invite-to-channel/${user.id}`, { channel_id: selectedChannel, message: inviteMsg || null });
      toast.success("Davet gonderildi");
      setSelectedChannel("");
      setInviteMsg("");
    } catch (err) { toast.error(err.response?.data?.detail || "Davet gonderilemedi"); }
    finally { setInviting(false); }
  };

  const userGroups = groups.filter(g => user.group_ids?.includes(g.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-[#27272A] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#24A1DE]/10 flex items-center justify-center text-[#24A1DE] font-bold">
              {(user.first_name || user.username || "?")[0]?.toUpperCase()}
            </div>
            <div>
              <span>{user.first_name || "Anonim"} {user.last_name || ""}</span>
              {user.username && <p className="text-sm text-[#24A1DE] font-mono font-normal">@{user.username}</p>}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 mb-4" data-testid="profile-tabs">
          {[
            { key: "info", label: "Bilgiler" },
            { key: "message", label: "Mesaj" },
            { key: "invite", label: "Davet" },
          ].map(t => (
            <button
              key={t.key}
              data-testid={`profile-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${
                activeTab === t.key
                  ? "bg-[#24A1DE] text-white"
                  : "bg-[#2C2C2E] text-[#A1A1AA] hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "info" && (
          <div className="space-y-3" data-testid="profile-info">
            <div className="grid grid-cols-2 gap-3">
              <InfoCard label="Telegram ID" value={user.telegram_id || "-"} mono />
              <InfoCard label="Durum" value={user.status === "active" ? "Aktif" : "Pasif"} color={user.status === "active" ? "#34C759" : "#FF3B30"} />
              <InfoCard label="Rol" value={user.role || "member"} />
              <InfoCard label="Kaynak" value={user.source || "-"} />
            </div>
            {user.username && (
              <a
                href={`https://t.me/${user.username}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="telegram-profile-link"
                className="flex items-center gap-2 p-3 rounded-lg bg-[#24A1DE]/5 border border-[#24A1DE]/20 text-[#24A1DE] text-sm hover:bg-[#24A1DE]/10 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Telegram Profilini Ac
              </a>
            )}
            {userGroups.length > 0 && (
              <div>
                <p className="text-xs text-[#A1A1AA] mb-2">Gruplar</p>
                <div className="flex gap-1.5 flex-wrap">
                  {userGroups.map(g => (
                    <Badge key={g.id} variant="outline" style={{ borderColor: g.color, color: g.color }}>{g.name}</Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="text-xs text-[#A1A1AA]/60 pt-2 border-t border-[#27272A]">
              Kayit: {user.created_at ? new Date(user.created_at).toLocaleString("tr-TR") : "-"}
            </div>
          </div>
        )}

        {activeTab === "message" && (
          <div className="space-y-3" data-testid="profile-message">
            <p className="text-xs text-[#A1A1AA]">Bu kullaniciya dogrudan Telegram mesaji gonderin.</p>
            <Textarea
              data-testid="user-message-content"
              value={msgContent}
              onChange={(e) => setMsgContent(e.target.value)}
              className="bg-[#2C2C2E] border-transparent text-white min-h-[100px] resize-none"
              placeholder="Mesajinizi yazin..."
            />
            <Button
              data-testid="send-user-message-btn"
              onClick={sendMsg}
              disabled={sending || !msgContent.trim() || !user.telegram_id}
              className="w-full bg-[#24A1DE] hover:brightness-110 text-white"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? "Gonderiliyor..." : "Mesaj Gonder"}
            </Button>
            {!user.telegram_id && (
              <p className="text-xs text-[#FF3B30] flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Telegram ID bulunamadi, mesaj gonderilemez.</p>
            )}
          </div>
        )}

        {activeTab === "invite" && (
          <div className="space-y-3" data-testid="profile-invite">
            <p className="text-xs text-[#A1A1AA]">Bu kullaniciyi bir yan kanala davet edin.</p>
            <Select value={selectedChannel} onValueChange={setSelectedChannel}>
              <SelectTrigger data-testid="invite-channel-select" className="bg-[#2C2C2E] border-transparent text-white">
                <SelectValue placeholder="Kanal secin" />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                {channels.map(ch => (
                  <SelectItem key={ch.id} value={ch.id}>
                    <span className="flex items-center gap-2"><Hash className="w-3 h-3" />{ch.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              data-testid="invite-custom-message"
              value={inviteMsg}
              onChange={(e) => setInviteMsg(e.target.value)}
              className="bg-[#2C2C2E] border-transparent text-white min-h-[70px] resize-none"
              placeholder="Ozel davet mesaji (opsiyonel)"
            />
            <Button
              data-testid="send-invite-btn"
              onClick={inviteToChannel}
              disabled={inviting || !selectedChannel || !user.telegram_id}
              className="w-full bg-[#34C759] hover:brightness-110 text-white"
            >
              {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {inviting ? "Gonderiliyor..." : "Davet Gonder"}
            </Button>
            {channels.length === 0 && (
              <p className="text-xs text-[#FF9F0A] flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Henuz yan kanal eklenmemis.</p>
            )}
            {!user.telegram_id && (
              <p className="text-xs text-[#FF3B30] flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Telegram ID bulunamadi.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InfoCard({ label, value, mono, color }) {
  return (
    <div className="p-2.5 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
      <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm font-medium ${mono ? "font-mono" : ""}`} style={{ color: color || "white" }}>{value}</p>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importingAll, setImportingAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [form, setForm] = useState({ username: "", first_name: "", last_name: "" });
  const [profileUser, setProfileUser] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkDM, setShowBulkDM] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadData(); checkRunningImport(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, groupsRes, setupRes, statsRes, channelsRes, apiRes] = await Promise.all([
        api.get("/users"),
        api.get("/groups"),
        api.get("/settings/telegram"),
        api.get("/users/stats"),
        api.get("/channels"),
        api.get("/settings/telegram-api").catch(() => ({ data: {} }))
      ]);
      setUsers(usersRes.data.users);
      setGroups(groupsRes.data.groups);
      setIsConfigured(setupRes.data.is_configured);
      setUserStats(statsRes.data);
      setChannels(channelsRes.data.channels);
      setHasSession(apiRes.data.has_session || false);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchFiltered = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterGroup !== "all") params.set("group_id", filterGroup);
      if (filterStatus !== "all") params.set("status", filterStatus);
      const { data } = await api.get(`/users?${params.toString()}`);
      setUsers(data.users);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filterGroup, filterStatus]);

  useEffect(() => {
    const t = setTimeout(() => { if (!loading || filterGroup !== "all" || filterStatus !== "all") fetchFiltered(); }, 300);
    return () => clearTimeout(t);
  }, [filterGroup, filterStatus]);

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Kullanici eklendi");
      setShowAdd(false);
      setForm({ username: "", first_name: "", last_name: "" });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Kullanici silindi");
      loadData();
    } catch (err) { toast.error("Silme basarisiz"); }
  };

  const toggleStatus = async (id) => {
    try {
      const { data } = await api.post(`/users/toggle-status/${id}`);
      toast.success(data.message);
      loadData();
    } catch (err) { toast.error("Durum guncellenemedi"); }
  };

  const assignGroup = async (userId, groupId) => {
    const user = users.find(u => u.id === userId);
    const groupIds = user.group_ids?.includes(groupId)
      ? user.group_ids.filter(g => g !== groupId)
      : [...(user.group_ids || []), groupId];
    try {
      await api.put(`/users/${userId}`, { group_ids: groupIds });
      toast.success("Grup guncellendi");
      loadData();
    } catch (err) { toast.error("Guncelleme basarisiz"); }
  };

  const importUsers = async () => {
    if (!isConfigured) {
      toast.error("Once Ayarlar sayfasindan Telegram bot'unu baglayiniz");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/users/import");
      setImportResult(data);
      toast.success(data.message);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Ice aktarma basarisiz"); }
    finally { setImporting(false); }
  };

  const checkRunningImport = async () => {
    try {
      const { data } = await api.get("/users/import-status");
      if (data.running || (data.status && data.status !== "completed" && !data.status?.startsWith("error") && Object.keys(data).length > 1)) {
        setImportingAll(true);
        setImportResult(data);
        pollImportStatus();
      } else if (data.status === "completed") {
        setImportResult(data);
      }
    } catch { /* ignore */ }
  };

  const importAllMembers = async () => {
    setImportingAll(true);
    setImportResult(null);
    try {
      const { data } = await api.post("/users/import-all-members");
      if (data.status === "started" || data.status === "running") {
        toast.success("Ice aktarma arka planda basladi");
        setImportResult(data.progress || { status: "Baslatiliyor...", channels_done: 0, channels_total: 0, imported: 0, total_found: 0 });
        pollImportStatus();
      } else {
        setImportResult(data);
        toast.success(data.message);
        loadData();
        setImportingAll(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ice aktarma basarisiz");
      setImportingAll(false);
    }
  };

  const pollImportStatus = () => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/users/import-status");
        setImportResult(data);
        if (data.status === "completed" || data.status?.startsWith("error")) {
          clearInterval(interval);
          setImportingAll(false);
          loadData();
          if (data.status === "completed") toast.success("Ice aktarma tamamlandi!");
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  const openProfile = (user) => {
    setProfileUser(user);
    setShowProfile(true);
  };

  const toggleSelect = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) setSelectedUsers(new Set());
    else setSelectedUsers(new Set(users.map(u => u.id)));
  };

  const sendBulkDMToSelected = async () => {
    if (!bulkMsg.trim() || selectedUsers.size === 0) return;
    setBulkSending(true);
    try {
      const { data } = await api.post("/messages/bulk-dm", {
        content: bulkMsg,
        user_ids: Array.from(selectedUsers)
      });
      toast.success(data.message);
      setShowBulkDM(false);
      setBulkMsg("");
      setSelectedUsers(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "DM gonderilemedi");
    } finally { setBulkSending(false); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Kullanicilar</h1>
        <div className="flex gap-2">
          {selectedUsers.size > 0 && (
            <Button data-testid="bulk-dm-selected-btn" onClick={() => setShowBulkDM(true)} className="bg-[#34C759] hover:brightness-110 text-white">
              <Send className="w-4 h-4 mr-2" />{selectedUsers.size} Seciliye DM Gonder
            </Button>
          )}
          {hasSession && (
            <Button data-testid="import-all-members-btn" onClick={importAllMembers} disabled={importingAll} className="bg-[#AF52DE] hover:brightness-110 text-white">
              {importingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
              {importingAll ? "Aktariliyor..." : "Tum Aboneleri Ice Aktar"}
            </Button>
          )}
          {isConfigured && (
            <Button data-testid="import-users-btn" onClick={importUsers} disabled={importing} variant="outline" className="border-[#24A1DE]/30 text-[#24A1DE] hover:bg-[#24A1DE]/10 hover:text-white">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {importing ? "Aktariliyor..." : "Yoneticileri Ice Aktar"}
            </Button>
          )}
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="bg-[#24A1DE] hover:brightness-110 text-white">
                <Plus className="w-4 h-4 mr-2" />Kullanici Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Kullanici Ekle</DialogTitle>
              </DialogHeader>
              <form onSubmit={addUser} className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-[#A1A1AA] text-sm">Telegram Kullanici Adi</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#24A1DE] text-sm font-medium">@</span>
                    <Input data-testid="user-username" value={form.username} onChange={(e) => setForm({...form, username: e.target.value.replace("@", "")})} className="bg-[#2C2C2E] border-transparent text-white pl-8" placeholder="kullanici_adi" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[#A1A1AA] text-sm">Ad</Label>
                    <Input data-testid="user-first-name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[#A1A1AA] text-sm">Soyad</Label>
                    <Input data-testid="user-last-name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" />
                  </div>
                </div>
                {isConfigured && (
                  <p className="text-xs text-[#34C759] flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#34C759]" />
                    Bot bagli - bilgiler otomatik cozulecek
                  </p>
                )}
                <Button data-testid="submit-add-user" type="submit" className="w-full bg-[#24A1DE] hover:brightness-110">Ekle</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {userStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6" data-testid="user-stats">
          <StatCard icon={Users} label="Toplam" value={userStats.total} color="#24A1DE" />
          <StatCard icon={UserCheck} label="Aktif" value={userStats.active} color="#34C759" />
          <StatCard icon={UserX} label="Pasif" value={userStats.inactive} color="#FF3B30" />
          <StatCard icon={Layers} label="Grupsuz" value={userStats.ungrouped} color="#FF9F0A" />
          {userStats.group_stats?.slice(0, 3).map(gs => (
            <Card key={gs.id} className="bg-[#121212] border-[#27272A]">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${gs.color}15` }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: gs.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-[#A1A1AA] truncate">{gs.name}</p>
                  <p className="text-lg font-bold text-white font-heading">{gs.count}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <Card className="bg-[#121212] border-[#34C759]/20 mb-4" data-testid="import-result">
          <CardContent className="p-4">
            {importResult.status === "completed" ? (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <UserCheck className="w-5 h-5 text-[#34C759]" />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm">{importResult.message}</span>
                    {importResult.total_in_db && <span className="text-[#A1A1AA] text-xs ml-2">(DB'de toplam: {importResult.total_in_db})</span>}
                  </div>
                  <Button size="sm" variant="ghost" className="text-[#A1A1AA] h-6 text-xs" onClick={() => setImportResult(null)}>Kapat</Button>
                </div>
                {importResult.sources?.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {importResult.sources.map((s, i) => (
                      <Badge key={i} variant="outline" className={s.error ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#34C759] text-[#34C759]"}>
                        {s.channel}: {s.error || `${s.found || 0} bulundu, ${s.imported || 0} yeni`}
                      </Badge>
                    ))}
                  </div>
                )}
              </>
            ) : importResult.status?.startsWith("error") ? (
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-[#FF3B30]" />
                <span className="text-[#FF3B30] text-sm">{importResult.status}</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-[#24A1DE] animate-spin" />
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{importResult.status || "Baslatiliyor..."}</p>
                    <p className="text-[#A1A1AA] text-xs">
                      {importResult.channels_done || 0}/{importResult.channels_total || "?"} kanal | {importResult.imported || 0} yeni abone | {importResult.total_found || 0} toplam bulundu
                    </p>
                  </div>
                </div>
                {importResult.channels_total > 0 && (
                  <div className="w-full bg-[#27272A] rounded-full h-1.5">
                    <div className="bg-[#24A1DE] h-1.5 rounded-full transition-all" style={{ width: `${((importResult.channels_done || 0) / importResult.channels_total) * 100}%` }} />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* No Users Banner */}
      {users.length === 0 && !loading && !search && filterGroup === "all" && filterStatus === "all" && (
        <Card className="bg-[#121212] border-[#24A1DE]/30 mb-6" data-testid="import-banner">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-[#24A1DE]/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-[#24A1DE]" />
              </div>
              <div>
                <h3 className="text-white font-heading text-lg font-semibold mb-1">Kullanicilarinizi Ice Aktarin</h3>
                <p className="text-[#A1A1AA] text-sm max-w-md">
                  {isConfigured
                    ? hasSession
                      ? "Tum kanallarinizdaki aboneleri otomatik olarak ice aktarin."
                      : "Yoneticileri ice aktarabilir veya Ayarlar > Telegram API'den tum aboneleri aktarabilirsiniz."
                    : "Once Ayarlar sayfasindan Telegram bot'unuzu baglayiniz."}
                </p>
              </div>
              {isConfigured ? (
                <div className="flex gap-2 flex-wrap justify-center">
                  {hasSession && (
                    <Button data-testid="import-all-banner-btn" onClick={importAllMembers} disabled={importingAll} className="bg-[#AF52DE] hover:brightness-110 text-white h-10 px-6">
                      {importingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                      {importingAll ? "Ice Aktariliyor..." : "Tum Aboneleri Ice Aktar"}
                    </Button>
                  )}
                  <Button data-testid="import-users-banner-btn" onClick={importUsers} disabled={importing} variant={hasSession ? "outline" : "default"}
                    className={hasSession ? "border-[#24A1DE]/30 text-[#24A1DE] hover:bg-[#24A1DE]/10 h-10 px-6" : "bg-[#24A1DE] hover:brightness-110 text-white h-10 px-6"}>
                    {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    {importing ? "Ice Aktariliyor..." : "Yoneticileri Ice Aktar"}
                  </Button>
                  {!hasSession && (
                    <Button variant="ghost" onClick={() => navigate("/settings")} className="text-[#AF52DE] h-10 px-4 text-sm">
                      Tum aboneler icin Telegram API baglayin
                    </Button>
                  )}
                </div>
              ) : (
                <Button data-testid="go-settings-btn" onClick={() => navigate("/settings")} className="bg-[#FF9F0A] hover:brightness-110 text-white h-10 px-6">
                  <AlertCircle className="w-4 h-4 mr-2" />Once Telegram'i Baglayin
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filter Bar */}
      {(users.length > 0 || search || filterGroup !== "all" || filterStatus !== "all") && (
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              data-testid="search-users"
              placeholder="Kullanici ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchFiltered()}
              className="pl-10 bg-[#121212] border-[#27272A] text-white h-9"
            />
          </div>
          <Button data-testid="search-btn" variant="outline" onClick={fetchFiltered} className="border-[#27272A] text-[#A1A1AA] hover:text-white h-9">Ara</Button>
          <Select value={filterGroup} onValueChange={setFilterGroup}>
            <SelectTrigger data-testid="filter-group" className="w-[160px] bg-[#121212] border-[#27272A] text-white h-9">
              <Filter className="w-3 h-3 mr-1.5 text-[#A1A1AA]" />
              <SelectValue placeholder="Grup" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
              <SelectItem value="all">Tum Gruplar</SelectItem>
              {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="w-[130px] bg-[#121212] border-[#27272A] text-white h-9">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
              <SelectItem value="all">Tum Durum</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="inactive">Pasif</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline" className="border-[#27272A] text-[#A1A1AA] h-9 flex items-center px-3">
            {users.length} kullanici
          </Badge>
        </div>
      )}

      {/* User Table */}
      {users.length > 0 && (
        <Card className="bg-[#121212] border-[#27272A]" data-testid="users-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#27272A] hover:bg-transparent">
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="select-all-users"
                      checked={users.length > 0 && selectedUsers.size === users.length}
                      onCheckedChange={toggleSelectAll}
                      className="border-[#A1A1AA]"
                    />
                  </TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Kullanici</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Telegram ID</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Rol</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Gruplar</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Kaynak</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Durum</TableHead>
                  <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-right">Islem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-[#27272A] hover:bg-[#2C2C2E]/50 cursor-pointer" data-testid={`user-row-${user.id}`} onClick={() => openProfile(user)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        data-testid={`select-user-${user.id}`}
                        checked={selectedUsers.has(user.id)}
                        onCheckedChange={() => toggleSelect(user.id)}
                        className="border-[#A1A1AA]"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#24A1DE]/10 flex items-center justify-center shrink-0 text-[#24A1DE] text-xs font-bold">
                          {(user.first_name || user.username || "?")[0]?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{user.first_name || "Anonim"} {user.last_name || ""}</p>
                          <p className="text-[#24A1DE] text-xs font-mono">@{user.username || "-"}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-[#A1A1AA]">{user.telegram_id || "-"}</TableCell>
                    <TableCell>
                      {user.role && user.role !== "member" && (
                        <Badge variant="outline" className="border-[#FF9F0A] text-[#FF9F0A] text-xs">{user.role}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.group_ids?.length > 0 ? user.group_ids.map(gid => {
                          const g = groups.find(gr => gr.id === gid);
                          return g ? (
                            <Badge key={gid} variant="outline" style={{ borderColor: g.color, color: g.color }} className="text-[10px] h-5">{g.name}</Badge>
                          ) : null;
                        }) : (
                          <span className="text-xs text-[#A1A1AA]/50">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.source && <span className="text-xs text-[#A1A1AA]">{user.source}</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={user.status === "active" ? "border-[#34C759] text-[#34C759]" : "border-[#FF3B30] text-[#FF3B30]"}>
                        {user.status === "active" ? "Aktif" : "Pasif"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid={`user-actions-${user.id}`} variant="ghost" className="h-8 w-8 p-0 text-[#A1A1AA] hover:text-white">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1C1C1E] border-[#27272A] w-52">
                          <DropdownMenuItem data-testid={`view-profile-${user.id}`} onClick={() => openProfile(user)} className="text-white hover:bg-[#2C2C2E] cursor-pointer">
                            <Eye className="w-4 h-4 mr-2 text-[#24A1DE]" />Profili Gor
                          </DropdownMenuItem>
                          {user.username && (
                            <DropdownMenuItem onClick={() => window.open(`https://t.me/${user.username}`, "_blank")} className="text-white hover:bg-[#2C2C2E] cursor-pointer">
                              <ExternalLink className="w-4 h-4 mr-2 text-[#24A1DE]" />Telegram'da Ac
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`send-msg-${user.id}`} onClick={() => { setProfileUser(user); setShowProfile(true); }} className="text-white hover:bg-[#2C2C2E] cursor-pointer">
                            <Send className="w-4 h-4 mr-2 text-[#34C759]" />Mesaj Gonder
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`toggle-status-${user.id}`} onClick={() => toggleStatus(user.id)} className="text-white hover:bg-[#2C2C2E] cursor-pointer">
                            <ToggleLeft className="w-4 h-4 mr-2 text-[#FF9F0A]" />{user.status === "active" ? "Pasif Yap" : "Aktif Yap"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#27272A]" />
                          <p className="px-2 py-1 text-[10px] text-[#A1A1AA] uppercase tracking-wider">Gruba Ekle/Cikar</p>
                          {groups.map(g => (
                            <DropdownMenuItem key={g.id} data-testid={`assign-group-${user.id}-${g.id}`} onClick={() => assignGroup(user.id, g.id)} className="text-white hover:bg-[#2C2C2E] cursor-pointer">
                              <UserPlus className="w-4 h-4 mr-2" style={{ color: g.color }} />
                              {user.group_ids?.includes(g.id) ? `${g.name}'dan cikar` : `${g.name}'a ekle`}
                            </DropdownMenuItem>
                          ))}
                          {channels.length > 0 && (
                            <>
                              <DropdownMenuSeparator className="bg-[#27272A]" />
                              <p className="px-2 py-1 text-[10px] text-[#A1A1AA] uppercase tracking-wider">Kanala Davet</p>
                              {channels.map(ch => (
                                <DropdownMenuItem key={ch.id} data-testid={`invite-to-${user.id}-${ch.id}`} onClick={async () => {
                                  if (!user.telegram_id) { toast.error("Telegram ID bulunamadi"); return; }
                                  try {
                                    await api.post(`/users/invite-to-channel/${user.id}`, { channel_id: ch.id });
                                    toast.success(`${ch.name} kanalina davet gonderildi`);
                                  } catch (err) { toast.error(err.response?.data?.detail || "Davet gonderilemedi"); }
                                }} className="text-white hover:bg-[#2C2C2E] cursor-pointer">
                                  <Hash className="w-4 h-4 mr-2 text-[#24A1DE]" />{ch.name}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                          <DropdownMenuSeparator className="bg-[#27272A]" />
                          <DropdownMenuItem data-testid={`delete-user-${user.id}`} onClick={() => deleteUser(user.id)} className="text-[#FF3B30] hover:bg-[#2C2C2E] cursor-pointer">
                            <Trash2 className="w-4 h-4 mr-2" />Sil
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty filter result */}
      {users.length === 0 && !loading && (search || filterGroup !== "all" || filterStatus !== "all") && (
        <Card className="bg-[#121212] border-[#27272A]">
          <CardContent className="p-8 text-center">
            <Search className="w-8 h-8 text-[#A1A1AA] mx-auto mb-3" />
            <p className="text-[#A1A1AA] text-sm">Aramaniza uygun kullanici bulunamadi</p>
            <Button variant="ghost" className="text-[#24A1DE] mt-2 text-sm h-8" onClick={() => { setSearch(""); setFilterGroup("all"); setFilterStatus("all"); loadData(); }}>
              Filtreleri Temizle
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Bulk DM Dialog */}
      <Dialog open={showBulkDM} onOpenChange={setShowBulkDM}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Send className="w-5 h-5 text-[#34C759]" />{selectedUsers.size} Kullaniciya DM Gonder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              data-testid="bulk-dm-message"
              value={bulkMsg}
              onChange={(e) => setBulkMsg(e.target.value)}
              className="bg-[#2C2C2E] border-transparent text-white min-h-[120px] resize-none"
              placeholder="Mesajinizi yazin..."
            />
            <Button
              data-testid="confirm-bulk-dm-btn"
              onClick={sendBulkDMToSelected}
              disabled={bulkSending || !bulkMsg.trim()}
              className="w-full bg-[#34C759] hover:brightness-110 text-white"
            >
              {bulkSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {bulkSending ? "Gonderiliyor..." : "Gonder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Profile Dialog */}
      <UserProfileDialog
        user={profileUser}
        groups={groups}
        channels={channels}
        open={showProfile}
        onOpenChange={setShowProfile}
        onRefresh={loadData}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="bg-[#121212] border-[#27272A]">
      <CardContent className="p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div>
          <p className="text-xs text-[#A1A1AA]">{label}</p>
          <p className="text-lg font-bold font-heading" style={{ color }}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
